/**
 * EnvironmentalSections — renders the 13 EIA-specific sections in the report view.
 * Only shown when report.reportType === 'environmental'.
 */
import React from 'react';
import type { SiteRagReport } from '../../../types/siteRag';
import type { ReportTheme } from '../../../types/reportTheme';
import { AlertTriangle } from 'lucide-react';
import { walkCitationText } from '../../../lib/walkCitationText';
import { SectionQuarantineBanner } from './SectionQuarantineBanner';
import {
  permittingPathwayCardClass,
} from '../../../lib/reportTypography';
import {
  REPORT_SECTION_H2_CLASS,
  reportSectionHeadingStyle,
  reportTypographySizes, REPORT_TEXT_PX } from '../../../lib/reportSectionHeading';
import { roleColor } from '../../../lib/reportRoleColors';
import { EditorialBadge, EditorialCallout, type EditorialBadgeTone } from './editorial';
import { EditorialSectionShell } from './EditorialSectionShell';
import { REPORT_SECTION_PAGE_COLS_2_CLASS } from '../../../lib/reportSectionChrome';

const SUBHEAD_MONO = 'var(--report-body-font, inherit)';

/** Resolve a known heading scale, defaulting legacy/undefined to editorial. */
function resolveScale(
  scale: ReportTheme['headingScale'] | undefined,
): ReportTheme['headingScale'] {
  return scale === 'editorial' || scale === 'technical' || scale === 'compact'
    ? scale
    : 'editorial';
}

/**
 * R-EDIT (env-charts) — editorial chrome conversion of the LEGACY
 * monolithic environmental renderer (the fallback for archived reports
 * without the `nepaScreening` modern-sections sentinel).
 *
 * Heavy bordered sub-cards → hairline-TOPPED prose blocks with small-
 * caps mono labels; the NEPA determination box → EditorialCallout; the
 * ESA/CWA + mitigation-effectiveness + permit-level + risk-severity
 * pills → theme-aware EditorialBadge tags (positive / warning /
 * negative). All chrome reads roleColor(theme, ...) instead of literal
 * slate / success / danger / primary classes.
 *
 * NO DATA LOSS: every section, field, table column, tag, and
 * subsection that rendered before still renders. Only the chrome moves.
 */

/**
 * W3 (legacy shell-wrap) — optional props that let the legacy aggregate
 * participate in the W2 column engine + per-section chrome WHEN (and only
 * when) a theme preset is in play. Threaded from SiteRagReportView from
 * `presentation.presetId`, `sectionPageColumnCount`, and the inline-shell
 * flags. ALL default to the legacy no-op so the `!hasModernSections` +
 * no-preset path stays byte-identical (the gate is `presetActive`).
 */
interface ShellProps {
  /** True ONLY when `presentation.presetId != null`. Gates every wrap below. */
  presetActive?: boolean;
  /** Resolved body page-column count for this aggregate (1 | 2). */
  pageColumns?: 1 | 2;
  /** Per-section shell padding flag (mirrors inlineSectionShellPadding). */
  shellPadding?: boolean;
  /** Whether the left confidential rail is omitted (mirrors omitSectionSidebar). */
  omitSectionSidebar?: boolean;
}

interface Props extends ShellProps {
  report: SiteRagReport;
  theme: ReportTheme;
}

/** Risk-matrix severity → EditorialBadge tone. */
function severityTone(severity: string): EditorialBadgeTone {
  if (severity === 'Critical' || severity === 'High') return 'negative';
  if (severity === 'Medium') return 'warning';
  return 'positive';
}

/** Mitigation effectiveness → EditorialBadge tone (High = positive). */
function effectivenessTone(effectiveness: string): EditorialBadgeTone {
  const e = effectiveness.toLowerCase();
  if (e.startsWith('high')) return 'positive';
  if (e.startsWith('medium')) return 'warning';
  if (e.startsWith('low')) return 'negative';
  return 'neutral';
}

/** Permit jurisdiction level → EditorialBadge tone. */
function permitLevelTone(level: string): EditorialBadgeTone {
  if (level === 'Federal') return 'negative';
  if (level === 'State') return 'warning';
  return 'accent';
}

export const EnvironmentalSections: React.FC<Props> = ({
  report,
  theme,
  presetActive = false,
  pageColumns = 1,
  shellPadding = false,
  omitSectionSidebar = true,
}) => {
  const heading = (text: string) => (
    <h2
      className={`${REPORT_SECTION_H2_CLASS} mb-6`}
      style={reportSectionHeadingStyle(theme)}
      data-heading="true"
    >
      {text}
    </h2>
  );

  // Editorial small-caps mono sub-label in the theme ink — replaces the
  // old REPORT_FIELD_LABEL_CLASS micro-heading so it follows the preset.
  // The label TYPE (size / tracking / lead) now switches by
  // headingScale + density, mirroring EditorialSubsection's label chrome,
  // so each preset family reads structurally distinct instead of one fixed
  // `text-[11px]` / 0.14em across all presets.
  const scale = resolveScale(theme.headingScale);
  const dense = theme.density === 'compact';
  const subLabelStyle: React.CSSProperties =
    scale === 'compact'
      ? { fontFamily: SUBHEAD_MONO, fontSize: 9.5, letterSpacing: '0.1em', color: roleColor(theme, 'ink'), marginBottom: dense ? 4 : 6 }
      : scale === 'technical'
        ? { fontFamily: SUBHEAD_MONO, fontSize: 10.5, letterSpacing: '0.12em', color: roleColor(theme, 'ink'), marginBottom: dense ? 5 : 7 }
        : { fontFamily: SUBHEAD_MONO, fontSize: REPORT_TEXT_PX.annotation, letterSpacing: '0.14em', color: roleColor(theme, 'ink'), marginBottom: dense ? 6 : 8 };
  const subHeading = (text: string) => (
    <h3 className="font-semibold uppercase" style={subLabelStyle}>
      {text}
    </h3>
  );

  // Body prose color follows the theme; size derives from the resolved body
  // size (headingScale + themeTypographyScale aware) × bodyTextScale instead
  // of the fixed `text-sm` in REPORT_BODY_TEXT_CLASS.
  const bodyFontSize = `calc(${reportTypographySizes(theme).body} * ${theme.bodyTextScale ?? 1})`;
  const bodyText = (text: string | undefined) =>
    text ? (
      <p className="leading-relaxed mb-4" style={{ color: roleColor(theme, 'body'), fontSize: bodyFontSize }}>
        {walkCitationText(text, report.references)}
      </p>
    ) : null;

  // Two-column presets (theme.bodyColumns === 2) already split the page into
  // narrow columns, so the inner panel grids collapse to a SINGLE column to
  // avoid cramming a 2/3-up grid inside an already-narrow column. Single-
  // column presets keep the responsive multi-up grid.
  const twoColPreset = theme.bodyColumns === 2;
  const innerGridClass = (multi: 'lg:grid-cols-2' | 'lg:grid-cols-3') =>
    twoColPreset ? 'grid grid-cols-1' : `grid grid-cols-1 ${multi}`;

  // Themed inner panel — a hairline-bordered content block on the cream page.
  // FLATTEN-TO-CREAM: the panel no longer carries a wash FILL (the grey
  // `subtleBg` default, nor the `accentSoft` / `positiveSoft` emphasis washes).
  // It reads as ink-on-cream; the hairline frame delineates it, and a toned
  // `accent` / `positive` LEFT-rule provides the ESA §7 / CWA §404 emphasis
  // (matching the EditorialRecommendations left-rule treatment) — no filled box.
  const innerPanel = (
    children: React.ReactNode,
    tone?: 'accent' | 'positive',
  ): React.ReactElement => {
    // William QA 2026-06-28 — left accent rule removed report-wide
    return (
      <div className="py-1" style={{ background: 'transparent' }}>
        {children}
      </div>
    );
  };

  const body = (
    <div className="space-y-10" style={{ fontFamily: theme.bodyFont, color: roleColor(theme, 'body') }}>

      {/* ═══ NEPA/CEQA SCREENING ═══ */}
      {/* RTB-2 (2026-05-18) — stamp `id` + `data-section` + `data-section-id`
           on every environmental section wrapper so the TOC's
           `#nepaScreening`-style anchors resolve. The bug surfaced on
           Redhead Feasibility v4.0 against keyFindings; the underlying
           pattern (section ids in the registry without DOM anchors)
           was identical here for all 11 env sections. */}
      {report.nepaScreening && (
        <div id="nepaScreening" data-section="nepaScreening" data-section-id="nepaScreening">
          {heading('NEPA/CEQA Screening')}
          {/* SNT-3 — quarantine banner for NEPA rationale. */}
          <SectionQuarantineBanner
            sectionId="nepaScreening.rationale"
            sectionLabel="NEPA Rationale"
            guardStatus={report.sectionGuardStatus?.['nepaScreening.rationale']}
            quarantinedOriginal={report.quarantinedOriginal?.['nepaScreening.rationale']}
            renderedText={report.nepaScreening.rationale}
          />
          {report.nepaScreening.determination && (
            <div className="mb-4">
              {/* Determination tier is a NEUTRAL procedural classification,
                  not a warning — its left-rule/label is a DECORATIVE accent
                  that tracks the theme brand colour (was `warning`, which
                  showed the theme's amber/brown status hue off-theme). */}
              <EditorialCallout theme={theme} tone="accent" label="Determination">
                <div className="text-base font-bold" style={{ color: roleColor(theme, 'ink') }}>
                  {report.nepaScreening.determination}
                </div>
                {report.nepaScreening.estimatedTimeline && (
                  <div className="text-xs mt-1" style={{ color: roleColor(theme, 'muted') }}>
                    Estimated timeline: {report.nepaScreening.estimatedTimeline}
                  </div>
                )}
              </EditorialCallout>
            </div>
          )}
          {bodyText(report.nepaScreening.rationale)}
          {subHeading('CEQA Applicability')}
          {bodyText(report.nepaScreening.ceqaApplicability)}
          {report.nepaScreening.keyTriggers?.length > 0 && (
            <div className="mt-2">
              {subHeading('Key Environmental Triggers')}
              <ul className="space-y-1">
                {report.nepaScreening.keyTriggers.map((t, i) => (
                  <li
                    key={`item-${i}`}
                    className="flex items-start gap-2 text-sm"
                    style={{ color: roleColor(theme, 'body') }}
                  >
                    <AlertTriangle
                      className="w-3.5 h-3.5 mt-0.5 shrink-0"
                      style={{ color: roleColor(theme, 'warning') }}
                    />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ═══ ECOLOGICAL ASSESSMENT ═══ */}
      {report.ecologicalAssessment && (
        <div id="ecologicalAssessment" data-section="ecologicalAssessment" data-section-id="ecologicalAssessment">
          {heading('Ecological Assessment')}
          <div className={`${innerGridClass('lg:grid-cols-2')} gap-4`}>
            {innerPanel(<>
              {subHeading('Critical Habitat (ESA)')}
              {bodyText(report.ecologicalAssessment.criticalHabitatAnalysis)}
            </>)}
            {innerPanel(<>
              {subHeading('ESA-Listed Species')}
              {bodyText(report.ecologicalAssessment.esaSpeciesAssessment)}
            </>)}
            {innerPanel(<>
              {subHeading('Wetlands Delineation (CWA 404)')}
              {bodyText(report.ecologicalAssessment.wetlandsDelineation)}
            </>)}
            {innerPanel(<>
              {subHeading('Wildlife Corridors')}
              {bodyText(report.ecologicalAssessment.wildlifeCorridors)}
            </>)}
          </div>
          <div className={`${innerGridClass('lg:grid-cols-2')} gap-4 mt-4`}>
            {innerPanel(<>
              {subHeading('ESA Section 7 Requirements')}
              {bodyText(report.ecologicalAssessment.section7Requirements)}
            </>, 'positive')}
            {innerPanel(<>
              {subHeading('CWA Section 404 Requirements')}
              {bodyText(report.ecologicalAssessment.section404Requirements)}
            </>, 'accent')}
          </div>
        </div>
      )}

      {/* ═══ WATER RESOURCES ═══ */}
      {report.waterResources && (
        <div id="waterResources" data-section="waterResources" data-section-id="waterResources">
          {heading('Water Resources')}
          {subHeading('Water Stress Analysis')}
          {bodyText(report.waterResources.waterStressAnalysis)}
          {subHeading('Water Rights Assessment')}
          {bodyText(report.waterResources.waterRightsAssessment)}
          {subHeading('Groundwater & Surface Water')}
          {bodyText(report.waterResources.groundwaterSurfaceWater)}
          {subHeading('Thermal Discharge (CWA 316(a))')}
          {bodyText(report.waterResources.thermalDischarge)}
          {subHeading('Stormwater Management (NPDES/SWPPP)')}
          {bodyText(report.waterResources.stormwaterManagement)}
        </div>
      )}

      {/* ═══ AIR QUALITY & EMISSIONS ═══ */}
      {report.airQualityEmissions && (
        <div id="airQualityEmissions" data-section="airQualityEmissions" data-section-id="airQualityEmissions">
          {heading('Air Quality & Emissions')}
          <div className={`${innerGridClass('lg:grid-cols-2')} gap-4`}>
            {innerPanel(<>
              {subHeading('Baseline AQI')}
              {bodyText(report.airQualityEmissions.baselineAqi)}
            </>)}
            {innerPanel(<>
              {subHeading('Attainment Status')}
              {bodyText(report.airQualityEmissions.attainmentStatus)}
            </>)}
          </div>
          {subHeading('Criteria Pollutants')}
          {bodyText(report.airQualityEmissions.criteriaPollutants)}
          {subHeading('GHG Implications')}
          {bodyText(report.airQualityEmissions.ghgImplications)}
          {subHeading('Construction Emissions')}
          {bodyText(report.airQualityEmissions.constructionEmissions)}
          {subHeading('General Conformity (40 CFR 93)')}
          {bodyText(report.airQualityEmissions.generalConformity)}
        </div>
      )}

      {/* ═══ FLOOD & NATURAL HAZARDS ═══ */}
      {report.floodNaturalHazards && (
        <div id="floodNaturalHazards" data-section="floodNaturalHazards" data-section-id="floodNaturalHazards">
          {heading('Flood & Natural Hazards')}
          {subHeading('FEMA Flood Analysis')}
          {bodyText(report.floodNaturalHazards.femaFloodAnalysis)}
          {subHeading('Coastal Zone Management (CZMA)')}
          {bodyText(report.floodNaturalHazards.coastalZoneManagement)}
          {subHeading('Sea Level Rise')}
          {bodyText(report.floodNaturalHazards.seaLevelRise)}
          {subHeading('Seismic Impact')}
          {bodyText(report.floodNaturalHazards.seismicImpact)}
          {subHeading('Climate Resiliency')}
          {bodyText(report.floodNaturalHazards.climateResiliency)}
        </div>
      )}

      {/* ═══ CULTURAL & HISTORICAL ═══ */}
      {report.culturalHistorical && (
        <div id="culturalHistorical" data-section="culturalHistorical" data-section-id="culturalHistorical">
          {heading('Cultural & Historical Resources')}
          {subHeading('Tribal Consultation (NHPA Section 106)')}
          {bodyText(report.culturalHistorical.tribalConsultation)}
          {subHeading('Archaeological Sensitivity')}
          {bodyText(report.culturalHistorical.archaeologicalSensitivity)}
          {subHeading('SHPO Consultation')}
          {bodyText(report.culturalHistorical.shpoConsultation)}
        </div>
      )}

      {/* ═══ LAND USE & ZONING ═══ */}
      {report.landUseZoning && (
        <div id="landUseZoning" data-section="landUseZoning" data-section-id="landUseZoning">
          {heading('Land Use & Zoning')}
          {subHeading('Current Zoning')}
          {bodyText(report.landUseZoning.currentZoning)}
          {subHeading('Adjacent Land Uses')}
          {bodyText(report.landUseZoning.adjacentLandUses)}
          {subHeading('Compatibility Analysis')}
          {bodyText(report.landUseZoning.compatibilityAnalysis)}
        </div>
      )}

      {/* ═══ NOISE & VISUAL IMPACT ═══ */}
      {report.noiseVisualImpact && (
        <div id="noiseVisualImpact" data-section="noiseVisualImpact" data-section-id="noiseVisualImpact">
          {heading('Noise & Visual Impact')}
          <div className={`${innerGridClass('lg:grid-cols-3')} gap-4`}>
            {innerPanel(<>
              {subHeading('Construction Noise')}
              {bodyText(report.noiseVisualImpact.constructionNoise)}
            </>)}
            {innerPanel(<>
              {subHeading('Operational Noise')}
              {bodyText(report.noiseVisualImpact.operationalNoise)}
            </>)}
            {innerPanel(<>
              {subHeading('Visual Screening')}
              {bodyText(report.noiseVisualImpact.visualScreening)}
            </>)}
          </div>
        </div>
      )}

      {/* ═══ MITIGATION PLAN ═══ */}
      {report.mitigationPlan && report.mitigationPlan.length > 0 && (
        <div id="mitigationPlan" data-section="mitigationPlan" data-section-id="mitigationPlan">
          {heading('Environmental Mitigation Plan')}
          {/* data-span-columns: defensive — the legacy aggregate's own wide
              mitigation table spans both flow columns under a 2-col preset. */}
          <div data-span-columns className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              {/* Header rule in the theme heading color; labels small-caps mono. */}
              <tr style={{ borderBottom: `2px solid ${roleColor(theme, 'rule')}` }}>
                {['Impact', 'Mitigation Measure', 'Cost', 'Timeline', 'Effectiveness'].map((h, hi) => (
                  <th
                    key={h}
                    className={`text-left py-2 text-[11px] font-semibold uppercase ${
                      hi === 0 ? 'pr-2' : hi === 4 ? 'pl-2' : 'px-2'
                    }`}
                    style={{
                      fontFamily: 'var(--report-body-font, inherit)',
                      letterSpacing: '0.12em',
                      color: roleColor(theme, 'ink'),
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {report.mitigationPlan.map((m, i) => (
                <tr
                  key={`item-${i}`}
                  style={{
                    borderBottom: `1px solid ${roleColor(theme, 'hair')}`,
                    background:
                      theme.tableStyle === 'striped' && i % 2 === 1
                        ? roleColor(theme, 'subtleBg')
                        : undefined,
                  }}
                >
                  <td className="py-2 pr-2 font-bold" style={{ color: roleColor(theme, 'ink') }}>{m.impactCategory}</td>
                  <td className="py-2 px-2" style={{ color: roleColor(theme, 'body') }}>{m.measure}</td>
                  <td className="py-2 px-2" style={{ fontFamily: 'var(--report-body-font, inherit)', color: roleColor(theme, 'body') }}>{m.costEstimate}</td>
                  <td className="py-2 px-2" style={{ color: roleColor(theme, 'body') }}>{m.timeline}</td>
                  <td className="py-2 pl-2">
                    <EditorialBadge theme={theme} tone={effectivenessTone(m.effectiveness)} variant="outline">
                      {m.effectiveness}
                    </EditorialBadge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* ═══ PERMITTING PATHWAY ═══ */}
      {/* SECTION-PORT (11B, "findings + permit timeline") — the locked Env
           layout pairs the environmental FINDINGS (rendered above as editorial
           callouts/badges/prose) with an EditorialTimeline variant='gantt'
           permit mini-gantt. GAP (KNOWN): the gantt needs per-permit numeric
           offset/duration fractions (404 pre-app / delineation / review /
           mitigation as 0..1 of the schedule axis) — gen does NOT emit a
           permitting SCHEDULE. `report.permittingPathway[]` carries only
           `estimatedTimeline` STRINGS (e.g. "12-18 months") + a `criticalPath`
           flag, never numeric offsets, so per the MAP the gantt is OMITTED and
           the findings-led pathway timeline (the existing dot-rail list below)
           is rendered instead. No month offsets are invented. */}
      {/* GAP: permit-timeline offsets/durations (0..1 fractions of the schedule
              axis) are not generated — `permittingPathway` has no numeric
              start/width per permit. When gen emits a permit SCHEDULE, attach an
              EditorialTimeline variant='gantt' here (rows from start/width,
              tone='risk' for criticalPath). Do NOT derive offsets from the
              free-text estimatedTimeline. */}
      {report.permittingPathway && report.permittingPathway.length > 0 && (
        <div id="permittingPathway" data-section="permittingPathway" data-section-id="permittingPathway">
          {heading('Permitting Pathway')}
          <div className="space-y-2">
            {report.permittingPathway.map((p, i) => (
              <div
                key={`item-${i}`}
                className={permittingPathwayCardClass(Boolean(p.criticalPath))}
                // Critical-path left rule tracks the theme ACCENT (decorative
                // schedule emphasis), not the app-token amber/brown warning.
                style={p.criticalPath ? { borderLeftColor: roleColor(theme, 'accent') } : undefined}
              >
                <div className="flex flex-col items-center shrink-0">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{
                      background: p.criticalPath
                        ? roleColor(theme, 'accent')
                        : roleColor(theme, 'rule'),
                    }}
                  />
                  {i < report.permittingPathway!.length - 1 && (
                    <div className="w-0.5 h-8 mt-1" style={{ background: roleColor(theme, 'hair') }} />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-bold" style={{ color: roleColor(theme, 'ink') }}>{p.permit}</h4>
                    <EditorialBadge theme={theme} tone={permitLevelTone(p.level)} variant="outline">
                      {p.level}
                    </EditorialBadge>
                    {p.criticalPath && (
                      <EditorialBadge theme={theme} tone="accent" variant="outline">
                        Critical Path
                      </EditorialBadge>
                    )}
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: roleColor(theme, 'muted') }}>{p.agency} – {p.estimatedTimeline}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══ ENVIRONMENTAL RISK MATRIX ═══ */}
      {report.environmentalRiskMatrix && report.environmentalRiskMatrix.length > 0 && (
        <div id="environmentalRiskMatrix" data-section="environmentalRiskMatrix" data-section-id="environmentalRiskMatrix">
          {heading('Risk Assessment')}
          <div className={`${innerGridClass('lg:grid-cols-2')} gap-3`}>
            {report.environmentalRiskMatrix.map((r, i) => {
              // William QA 2026-06-28 — left accent rule removed report-wide
              const tone = severityTone(r.severity);
              return (
                <div
                  key={`item-${i}`}
                  className="py-2 space-y-2"
                  style={{
                    background: 'transparent',
                  }}
                >
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-bold" style={{ color: roleColor(theme, 'ink') }}>{r.risk}</h4>
                    <div className="flex items-center gap-1">
                      <EditorialBadge theme={theme} tone={tone} variant="outline">{r.severity}</EditorialBadge>
                      <span className="text-xs" style={{ color: roleColor(theme, 'body') }}>{r.likelihood}</span>
                    </div>
                  </div>
                  <div className="text-xs" style={{ color: roleColor(theme, 'muted') }}>{r.category}</div>
                  <div className="pt-2" style={{ borderTop: `1px solid ${roleColor(theme, 'hair')}` }}>
                    <div
                      className="mb-0.5 text-[11px] font-semibold uppercase"
                      style={{
                        fontFamily: 'var(--report-body-font, inherit)',
                        letterSpacing: '0.14em',
                        color: roleColor(theme, 'positive'),
                      }}
                    >
                      Mitigation
                    </div>
                    <p className="text-xs" style={{ color: roleColor(theme, 'muted') }}>{walkCitationText(r.mitigation, report.references)}</p>
                  </div>
                  <div className="text-xs" style={{ color: roleColor(theme, 'body') }}>Residual risk: <span className="font-bold">{r.residualRisk}</span></div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  // W3 (gated legacy shell-wrap) — when a theme preset is in play, wrap the
  // aggregate in EditorialSectionShell (classic mode) so it inherits per-section padding,
  // density, typography scaling, and the W2 column engine (data-section-columns
  // + the report-section-page-cols-2 class drive the multicol layout, and the
  // [data-span-columns] hooks added above let wide tables span both columns).
  // When NO preset is active the bare body renders unchanged (byte-identical
  // to the pre-W3 output) — this is the hard byte-stability invariant for
  // archived `!hasModernSections` reports.
  if (!presetActive) return body;

  return (
    <EditorialSectionShell
      theme={theme}
      shellPadding={shellPadding}
      omitSectionSidebar={omitSectionSidebar}
      data-section-columns={String(pageColumns)}
      extraClassName={pageColumns === 2 ? REPORT_SECTION_PAGE_COLS_2_CLASS : undefined}
    >
      {body}
    </EditorialSectionShell>
  );
};
